import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircleFilled, ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons'
import { api, registration, type PhoneSession } from '../api'

/**
 * The email address and phone number on the account: which are proven, and how
 * to prove or replace the other.
 *
 * Registration proves one of the two, so most accounts arrive here with one
 * confirmed and one not. A change only takes effect once the new value has
 * been proven — the panel says so, because "we will keep using the old one
 * until you confirm" is reassuring rather than obstructive, and it is what
 * stops a stolen session pointing password reset somewhere else.
 */
export default function ContactPanel() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const profile = useQuery({ queryKey: ['profile'], queryFn: api.profile })
  const [editing, setEditing] = useState<'email' | 'phone' | null>(null)
  const [value, setValue] = useState('')
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)
  const [sms, setSms] = useState<PhoneSession | null>(null)

  const p = profile.data

  const email = useMutation({
    mutationFn: (next?: string) => api.confirmEmail(next),
    onSuccess: r => {
      setNote({ text: t('linkSentTo', { email: r.sent_to }), kind: 'ok' })
      setEditing(null); setValue('')
    },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  const phone = useMutation({
    mutationFn: (next?: string) => api.confirmPhone(next),
    onSuccess: r => { setSms(r.phone); setEditing(null); setValue(''); setNote(null) },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  if (sms) {
    return <SmsStep session={sms} onDone={() => {
      setSms(null)
      qc.invalidateQueries({ queryKey: ['profile'] })
      setNote({ text: t('phoneConfirmed'), kind: 'ok' })
    }} onCancel={() => setSms(null)} />
  }

  if (!p) {
    return (
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('contactDetails')}</span></div>
        <div className="panel-body"><span className="muted">{t('loading')}</span></div>
      </div>
    )
  }

  const busy = email.isPending || phone.isPending

  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('contactDetails')}</span></div>
      <div className="panel-body stack">
        {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}

        <Row
          label={t('email')} value={p.email} verified={p.email_verified}
          available={p.can.email} busy={busy}
          editing={editing === 'email'} draft={value} onDraft={setValue}
          onConfirm={() => email.mutate(undefined)}
          onEdit={() => { setEditing('email'); setValue(p.email ?? ''); setNote(null) }}
          onCancel={() => { setEditing(null); setValue('') }}
          onSave={() => email.mutate(value)}
          inputType="email"
        />

        <Row
          label={t('phone')} value={p.phone} verified={p.phone_verified}
          available={p.can.phone} busy={busy}
          editing={editing === 'phone'} draft={value} onDraft={setValue}
          onConfirm={() => phone.mutate(undefined)}
          onEdit={() => { setEditing('phone'); setValue(p.phone ?? ''); setNote(null) }}
          onCancel={() => { setEditing(null); setValue('') }}
          onSave={() => phone.mutate(value)}
          inputType="tel"
        />

        <p className="field-hint">{t('changeKeepsOldUntilConfirmed')}</p>
      </div>
    </div>
  )
}

function Row({
  label, value, verified, available, busy, editing, draft, onDraft,
  onConfirm, onEdit, onCancel, onSave, inputType,
}: {
  label: string; value: string | null; verified: boolean; available: boolean; busy: boolean
  editing: boolean; draft: string; onDraft: (v: string) => void
  onConfirm: () => void; onEdit: () => void; onCancel: () => void; onSave: () => void
  inputType: 'email' | 'tel'
}) {
  const { t } = useTranslation()

  if (editing) {
    return (
      <div className="contact-row editing">
        <label className="contact-label" htmlFor={`c-${inputType}`}>{label}</label>
        <input
          id={`c-${inputType}`} className="contact-input" type={inputType} value={draft}
          onChange={e => onDraft(e.target.value)} inputMode={inputType === 'tel' ? 'tel' : 'email'}
          autoComplete={inputType === 'tel' ? 'tel' : 'email'} autoFocus
        />
        <div className="contact-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>{t('cancel')}</button>
          <button type="button" className="btn-small" onClick={onSave} disabled={busy || !draft.trim()}>
            {busy ? <LoadingOutlined /> : t('sendCode')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="contact-row">
      <div className="contact-main">
        <span className="contact-label">{label}</span>
        <span className="contact-value num">{value || <em className="muted">{t('noData')}</em>}</span>
      </div>
      <span className={`verify-badge ${verified ? 'ok' : 'pending'}`}>
        {verified ? <><CheckCircleFilled /> {t('verified')}</>
                  : <><ExclamationCircleFilled /> {t('unverified')}</>}
      </span>
      <div className="contact-actions">
        {!verified && available && (
          <button type="button" className="btn-small" onClick={onConfirm} disabled={busy}>
            {busy ? <LoadingOutlined /> : t('confirmNow')}
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={onEdit} disabled={busy || !available}>
          {t('changeContact')}
        </button>
      </div>
    </div>
  )
}

/** The verify.mn step again, inside Settings rather than on the way in. */
function SmsStep({ session, onDone, onCancel }: {
  session: PhoneSession; onDone: () => void; onCancel: () => void
}) {
  const { t } = useTranslation()
  const [left, setLeft] = useState(() => secondsLeft(session.expires_at))
  const [state, setState] = useState<'PENDING' | 'EXPIRED'>('PENDING')
  const stop = useRef(false)

  useEffect(() => {
    stop.current = false
    const poll = setInterval(async () => {
      if (stop.current) return
      try {
        const r = await registration.checkPhone(session.session_id)
        if (r.verified) { stop.current = true; onDone(); return }
        if (r.status === 'EXPIRED') { stop.current = true; setState('EXPIRED') }
      } catch { /* transient — keep asking until it expires */ }
    }, 3000)
    const clock = setInterval(() => {
      const s = secondsLeft(session.expires_at)
      setLeft(s)
      if (s <= 0 && !stop.current) { stop.current = true; setState('EXPIRED') }
    }, 1000)
    return () => { stop.current = true; clearInterval(poll); clearInterval(clock) }
  }, [session.session_id, session.expires_at, onDone])

  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('confirmPhone')}</span></div>
      <div className="panel-body stack">
        <p className="auth-lede">{session.instruction}</p>
        <div className="sms-code">
          <span className="sms-label">{t('sendThis')}</span>
          <b className="num">{session.code}</b>
          <span className="sms-label">{t('toShortcode', { shortcode: session.shortcode })}</span>
        </div>
        {state === 'EXPIRED' ? (
          <>
            <p className="form-note err">{t('codeExpired')}</p>
            <button type="button" className="btn-small" onClick={onCancel}>{t('back')}</button>
          </>
        ) : (
          <>
            <a className="auth-submit as-link" href={session.sms_uri}>{t('openMessages')}</a>
            <p className="field-hint">{t('smsCost')}</p>
            <p className="field-hint"><LoadingOutlined /> {t('waitingForSms', { seconds: left })}</p>
            <button type="button" className="btn-ghost" onClick={onCancel}>{t('cancel')}</button>
          </>
        )}
      </div>
    </div>
  )
}

function secondsLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 1000)) : 0
}
